import {
  PrivyProvider,
  useLogin,
  useLogout,
  usePrivy,
} from "@privy-io/react-auth";
import {
  CategoryScale,
  Chart as ChartJS,
  type ChartData,
  type ChartOptions,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import {
  CalendarDays,
  Check,
  Copy,
  ExternalLink,
  Handshake,
  LayoutDashboard,
  Link,
  LogOut,
  Percent,
  RefreshCw,
  SlidersHorizontal,
  TrendingUp,
  Users,
} from "lucide-react";
import { format, startOfDay, subDays } from "date-fns";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  partnersApi,
  PartnerDashboardFilters,
  PartnerDashboardResponse,
  PartnerMeResponse,
  PartnerSignupsResponse,
  SignupByUtm,
} from "./api";
import { fetchPublicConfig, type PublicConfig } from "./config";
import "./styles.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
);

type LoadState = "idle" | "loading" | "ready" | "forbidden" | "error";
type BootstrapState = LoadState;
type PartnerSection = "overview" | "links" | "sponsor";
type UtmField =
  | "utmSource"
  | "utmMedium"
  | "utmCampaign"
  | "utmContent"
  | "utmTerm";

const glassPanel =
  "rounded-[28px] border border-white/[0.16] bg-white/[0.055] shadow-2xl shadow-black/35 backdrop-blur-xl";
const glassTile =
  "rounded-3xl border border-white/[0.14] bg-white/[0.05] shadow-xl shadow-black/25 backdrop-blur-xl";
const brandGold = "#f3dc86";

const formatDate = (value: string | null) => {
  if (!value) {
    return "Not captured";
  }
  return format(new Date(value), "dd MMM yyyy, HH:mm");
};

const formatConversion = (completed: number, started: number) => {
  if (started === 0) {
    return "0%";
  }
  return `${Math.round((completed / started) * 100)}%`;
};

const attributionFilterKey = (filters: PartnerDashboardFilters) =>
  [
    [...(filters.utmSource ?? [])].sort().join(","),
    [...(filters.utmMedium ?? [])].sort().join(","),
    [...(filters.utmCampaign ?? [])].sort().join(","),
    [...(filters.utmContent ?? [])].sort().join(","),
    [...(filters.utmTerm ?? [])].sort().join(","),
  ].join("|");

const buildFilterOptions = (
  segments: SignupByUtm[],
  field: keyof PartnerDashboardFilters,
) =>
  Array.from(
    new Set(
      segments
        .map((segment) => segment[field])
        .filter((value): value is string => Boolean(value)),
    ),
  ).sort((left, right) => left.localeCompare(right));

async function copyText(value: string) {
  if (navigator.clipboard) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textArea = document.createElement("textarea");
  textArea.value = value;
  textArea.setAttribute("readonly", "true");
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand("copy");
  document.body.removeChild(textArea);
}

const buildJoinLink = (
  mainAppUrl: string,
  partnerCode: string,
  params?: Partial<Record<UtmField, string>>,
) => {
  const url = new URL(`${mainAppUrl.replace(/\/$/, "")}/football/join`);
  url.searchParams.set("partner_code", partnerCode);

  if (!params) {
    return url.toString();
  }

  const queryMap: Record<UtmField, string> = {
    utmSource: "utm_source",
    utmMedium: "utm_medium",
    utmCampaign: "utm_campaign",
    utmContent: "utm_content",
    utmTerm: "utm_term",
  };

  (Object.entries(params) as Array<[UtmField, string | undefined]>).forEach(
    ([field, value]) => {
      const trimmedValue = value?.trim();
      if (trimmedValue) {
        url.searchParams.set(queryMap[field], trimmedValue);
      }
    },
  );

  return url.toString();
};

function LoginScreen() {
  const { login } = useLogin();
  return (
    <AppFrame>
      <div className="mx-auto flex min-h-screen w-full max-w-[1180px] flex-col px-4 py-8 sm:px-6">
        <TopNav />
        <div className="flex flex-1 items-center justify-center" />
        <button
          className="mx-auto mb-6 mt-auto inline-flex min-h-12 w-full items-center justify-center rounded-xl bg-sdf-action-primary-bg px-5 text-base font-bold text-sdf-action-primary-fg transition hover:bg-sdf-action-primary-bg-hovered sm:max-w-[342px]"
          onClick={login}
        >
          Sign in
        </button>
      </div>
    </AppFrame>
  );
}

function TopNav({ actions }: { actions?: ReactNode }) {
  return (
    <header className="flex items-center justify-between gap-4">
      <BrandLockup />
      {actions ? (
        <div className="flex items-center gap-2">{actions}</div>
      ) : null}
    </header>
  );
}

function BrandLockup() {
  return (
    <div className="flex items-center gap-2">
      <img
        src="/images/sdf-logo-dark.svg"
        alt="Sport.Fun"
        className="h-11 w-11 shrink-0 sm:h-[52px] sm:w-[52px]"
      />
      <div>
        <div className="text-[28px] font-black uppercase leading-[0.92] tracking-[0.03em] text-sdf-text-primary sm:text-[34px]">
          Sport.Fun
        </div>
        <div
          className="mt-1 pl-0.5 text-[14px] uppercase leading-none tracking-[0.32em] text-[#f3dc86] sm:text-[16px]"
          style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
        >
          Partners
        </div>
      </div>
    </div>
  );
}

function AppFrame({ children }: { children: ReactNode }) {
  return (
    <main
      data-theme="dark"
      className="relative min-h-screen overflow-hidden bg-sdf-surface-canvas text-sdf-text-primary"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_38%,rgba(255,255,255,0.13),transparent_32%),radial-gradient(circle_at_72%_38%,rgba(108,171,221,0.16),transparent_27%)]" />
        <img
          src="/images/sdf-logo-dark.svg"
          alt=""
          className="absolute left-1/2 top-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 opacity-[0.065] sm:h-[720px] sm:w-[720px]"
        />
        <div className="absolute inset-x-0 top-0 h-72 bg-gradient-to-b from-white/[0.055] to-transparent" />
      </div>
      <div className="relative z-10">{children}</div>
    </main>
  );
}

function PartnerSidebar({
  me,
  mainAppUrl,
  activeSection,
  onSectionChange,
  onSignOut,
}: {
  me: PartnerMeResponse;
  mainAppUrl: string;
  activeSection: PartnerSection;
  onSectionChange: (section: PartnerSection) => void;
  onSignOut: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const joinLink = buildJoinLink(mainAppUrl, me.partner.partnerCode);

  const onCopy = useCallback(() => {
    void copyText(joinLink).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    });
  }, [joinLink]);

  return (
    <aside
      className={`${glassPanel} flex h-full min-h-0 flex-col gap-7 p-5 sm:p-6`}
    >
      <div>
        <p className="text-xs font-bold uppercase text-sdf-text-secondary">
          Partner
        </p>
        <h1 className="mt-2 text-2xl font-extrabold leading-tight tracking-normal text-sdf-text-primary">
          {me.partner.name}
        </h1>
      </div>

      <nav className="grid gap-3">
        {[
          {
            label: "Overview",
            icon: LayoutDashboard,
            value: "overview" as const,
          },
          { label: "Links", icon: Link, value: "links" as const },
          { label: "Sponsor", icon: Handshake, value: "sponsor" as const },
        ].map((item) => {
          const Icon = item.icon;
          const isActive = activeSection === item.value;
          return (
            <button
              key={item.label}
              className={`flex min-h-11 items-center gap-3 rounded-2xl px-3 text-left text-base font-bold transition ${
                isActive
                  ? "bg-white/[0.11] text-sdf-text-primary"
                  : "text-sdf-text-secondary hover:bg-white/[0.08] hover:text-sdf-text-primary"
              }`}
              onClick={() => onSectionChange(item.value)}
              type="button"
            >
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl border border-white/15 bg-white/[0.06]">
                <Icon size={16} />
              </span>
              {item.label}
            </button>
          );
        })}
      </nav>

      <div className="mt-auto grid gap-3 border-t border-white/10 pt-5">
        <button
          className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-sdf-action-primary-bg px-4 text-sm font-bold text-sdf-action-primary-fg transition hover:bg-sdf-action-primary-bg-hovered"
          onClick={onCopy}
          type="button"
        >
          {copied ? <Check size={16} /> : <Copy size={16} />}
          {copied ? "Copied" : "Copy link"}
        </button>
        <a
          className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.08] px-4 text-sm font-bold text-sdf-text-primary transition hover:bg-white/[0.13]"
          href={joinLink}
          target="_blank"
          rel="noreferrer"
        >
          Open link
          <ExternalLink size={16} />
        </a>
      </div>

      <button
        className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-white/[0.08] px-4 text-sm font-bold text-sdf-text-primary transition hover:bg-white/[0.13]"
        onClick={onSignOut}
        type="button"
      >
        <LogOut size={16} />
        Sign out
      </button>
    </aside>
  );
}

function LinksPanel({
  me,
  mainAppUrl,
}: {
  me: PartnerMeResponse;
  mainAppUrl: string;
}) {
  const [copied, setCopied] = useState(false);
  const [utmValues, setUtmValues] = useState<Record<UtmField, string>>({
    utmSource: "",
    utmMedium: "",
    utmCampaign: "",
    utmContent: "",
    utmTerm: "",
  });

  const generatedLink = buildJoinLink(
    mainAppUrl,
    me.partner.partnerCode,
    utmValues,
  );

  const updateField = (field: UtmField, value: string) => {
    setUtmValues((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const utmFields: Array<{
    field: UtmField;
    label: string;
    placeholder: string;
  }> = [
    { field: "utmCampaign", label: "Campaign", placeholder: "wc26" },
    { field: "utmSource", label: "Source", placeholder: "onefootball" },
    { field: "utmMedium", label: "Medium", placeholder: "web" },
    { field: "utmContent", label: "Content", placeholder: "hero" },
    { field: "utmTerm", label: "Term", placeholder: "launch" },
  ];

  const onCopy = useCallback(() => {
    void copyText(generatedLink).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    });
  }, [generatedLink]);

  return (
    <section className={`${glassPanel} grid gap-6 p-6 sm:p-8`}>
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-sdf-text-secondary">
          Link generator
        </p>
        <h2 className="mt-2 text-3xl font-extrabold leading-none tracking-normal text-sdf-text-primary">
          Build tracked join links
        </h2>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-sdf-text-secondary">
          Add UTM values to the partner join link, then copy the generated URL.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {utmFields.map(({ field, label, placeholder }) => (
          <label key={field} className="grid gap-2">
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-sdf-text-secondary">
              {label}
            </span>
            <input
              className="min-h-12 rounded-2xl border border-white/10 bg-white/[0.06] px-4 text-sm font-medium text-sdf-text-primary outline-none transition placeholder:text-sdf-text-secondary/60 focus:border-[#f3dc86] focus:bg-white/[0.08]"
              onChange={(event) => updateField(field, event.target.value)}
              placeholder={placeholder}
              type="text"
              value={utmValues[field]}
            />
          </label>
        ))}
      </div>

      <div className={`${glassTile} grid gap-4 p-5`}>
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-sdf-text-secondary">
              Generated link
            </p>
            <p className="mt-1 text-sm text-sdf-text-secondary">
              Partner code is always included.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-sdf-action-primary-bg px-4 text-sm font-bold text-sdf-action-primary-fg transition hover:bg-sdf-action-primary-bg-hovered"
              onClick={onCopy}
              type="button"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? "Copied" : "Copy link"}
            </button>
            <a
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.08] px-4 text-sm font-bold text-sdf-text-primary transition hover:bg-white/[0.13]"
              href={generatedLink}
              rel="noreferrer"
              target="_blank"
            >
              Open link
              <ExternalLink size={16} />
            </a>
          </div>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-white/10 bg-black/25 px-4 py-3 text-sm text-sdf-text-primary">
          <code className="break-all whitespace-pre-wrap font-medium">
            {generatedLink}
          </code>
        </div>
      </div>
    </section>
  );
}

function SponsorPanel() {
  return (
    <section className={`${glassPanel} grid place-items-center p-6 sm:p-8`}>
      <div className="max-w-md text-center">
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-sdf-text-secondary">
          Sponsor
        </p>
        <h2 className="mt-3 text-3xl font-extrabold leading-none tracking-normal text-sdf-text-primary">
          Sponsor tools are coming next.
        </h2>
      </div>
    </section>
  );
}

function PerformancePanel({
  dashboard,
  signups,
  segments,
  selectedFilters,
  onFilterChange,
}: {
  dashboard: PartnerDashboardResponse;
  signups: PartnerSignupsResponse;
  segments: SignupByUtm[];
  selectedFilters: PartnerDashboardFilters;
  onFilterChange: (filters: PartnerDashboardFilters) => void;
}) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draftFilters, setDraftFilters] =
    useState<PartnerDashboardFilters>(selectedFilters);
  const availableSegments =
    segments.length > 0 ? segments : dashboard.signupsByUtm;
  const campaignOptions = buildFilterOptions(availableSegments, "utmCampaign");
  const sourceOptions = buildFilterOptions(availableSegments, "utmSource");
  const mediumOptions = buildFilterOptions(availableSegments, "utmMedium");
  const contentOptions = buildFilterOptions(availableSegments, "utmContent");
  const termOptions = buildFilterOptions(availableSegments, "utmTerm");
  const chartValues = useMemo(() => {
    const today = startOfDay(new Date());
    const days = Array.from({ length: 30 }, (_, index) =>
      subDays(today, 29 - index),
    );
    const counts = new Map(days.map((day) => [format(day, "yyyy-MM-dd"), 0]));

    signups.signups.forEach((signup) => {
      if (!signup.redeemedAt) {
        return;
      }
      const key = format(startOfDay(new Date(signup.redeemedAt)), "yyyy-MM-dd");
      if (counts.has(key)) {
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    });

    return days.map((day) => ({
      label: format(day, "dd MMM"),
      value: counts.get(format(day, "yyyy-MM-dd")) ?? 0,
    }));
  }, [signups.signups]);
  const chartMax = Math.max(...chartValues.map((item) => item.value), 1);
  const chartData: ChartData<"line"> = {
    labels: chartValues.map((item) => item.label),
    datasets: [
      {
        data: chartValues.map((item) => item.value),
        borderColor: brandGold,
        borderWidth: 2.5,
        pointRadius: 0,
        pointHitRadius: 12,
        pointHoverRadius: 4,
        pointHoverBackgroundColor: brandGold,
        pointHoverBorderColor: "#ffffff",
        pointHoverBorderWidth: 2,
        tension: 0.35,
      },
    ],
  };
  const chartOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        displayColors: false,
        backgroundColor: "#050505",
        borderColor: "rgba(255,255,255,0.12)",
        borderWidth: 1,
        titleColor: "#ffffff",
        bodyColor: "#a3a3a3",
        callbacks: {
          label: (context) => `${context.parsed.y} completed onboarding`,
        },
      },
    },
    scales: {
      x: {
        display: false,
        grid: { display: false },
      },
      y: {
        display: false,
        min: 0,
        max: chartMax,
        grid: { display: false },
      },
    },
    elements: {
      line: {
        borderJoinStyle: "round",
        borderCapStyle: "round",
      },
    },
  };
  const selectedKey = attributionFilterKey(selectedFilters);
  const hasActiveFilters = selectedKey !== "";

  useEffect(() => {
    if (!filtersOpen) {
      setDraftFilters(selectedFilters);
    }
  }, [filtersOpen, selectedFilters]);

  const renderFilterGroup = (
    label: string,
    field: keyof PartnerDashboardFilters,
    options: string[],
  ) => {
    if (options.length === 0) {
      return null;
    }

    return (
      <div className="mt-3 first:mt-0">
        <p className="px-3 text-[11px] font-bold uppercase tracking-[0.14em] text-sdf-text-secondary">
          {label}
        </p>
        <div className="mt-2 grid gap-1">
          {options.map((option) => (
            <button
              key={`${field}-${option}`}
              className={`flex min-h-10 w-full items-center gap-3 rounded-xl px-3 text-left text-sm font-bold transition ${
                draftFilters[field]?.includes(option)
                  ? "bg-white/[0.12] text-sdf-text-primary"
                  : "text-sdf-text-primary hover:bg-white/[0.08]"
              }`}
              onClick={() => {
                setDraftFilters((current) => {
                  const currentValues = current[field] ?? [];
                  const nextValues = currentValues.includes(option)
                    ? currentValues.filter((value) => value !== option)
                    : [...currentValues, option];
                  const nextFilters = { ...current };
                  if (nextValues.length === 0) {
                    delete nextFilters[field];
                  } else {
                    nextFilters[field] = nextValues;
                  }
                  return nextFilters;
                });
              }}
              type="button"
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
                  draftFilters[field]?.includes(option)
                    ? "border-[#a78bfa] bg-[#a78bfa] text-black"
                    : "border-white/15 bg-white/[0.04] text-transparent"
                }`}
              >
                <Check size={13} />
              </span>
              <span className="min-w-0 truncate">{option}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <section
      className={`${glassPanel} relative grid h-full min-h-0 overflow-hidden p-6 sm:p-8`}
    >
      <div className="relative z-10 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sdf-text-secondary">
            <TrendingUp size={18} />
            <p className="text-xs font-bold uppercase">Performance</p>
          </div>
          <div className="mt-4 text-[52px] font-extrabold leading-none tracking-normal text-sdf-text-primary">
            {dashboard.totalSignups.toLocaleString()}
          </div>
          <p className="mt-2 text-sm text-sdf-text-secondary">
            Completed onboarding
          </p>
        </div>

        <div className="relative">
          <button
            className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-white/10 bg-white/[0.08] px-4 text-sm font-bold text-sdf-text-primary transition hover:bg-white/[0.13]"
            onClick={() => setFiltersOpen((value) => !value)}
          >
            <SlidersHorizontal size={16} />
            Filters
          </button>
          {filtersOpen ? (
            <div className="absolute right-0 top-12 z-20 flex max-h-[460px] w-[300px] flex-col overflow-hidden rounded-2xl border border-white/10 bg-black/90 shadow-2xl shadow-black/40 backdrop-blur-xl">
              <div className="overflow-y-auto p-2">
                <div className="px-3 pt-2 text-sm font-bold text-sdf-text-primary">
                  Attribution filters
                </div>
                {renderFilterGroup("Campaign", "utmCampaign", campaignOptions)}
                {renderFilterGroup("Source", "utmSource", sourceOptions)}
                {renderFilterGroup("Medium", "utmMedium", mediumOptions)}
                {renderFilterGroup("Content", "utmContent", contentOptions)}
                {renderFilterGroup("Term", "utmTerm", termOptions)}
                {campaignOptions.length === 0 &&
                sourceOptions.length === 0 &&
                mediumOptions.length === 0 &&
                contentOptions.length === 0 &&
                termOptions.length === 0 ? (
                  <p className="px-3 py-3 text-sm text-sdf-text-secondary">
                    No attribution segments yet.
                  </p>
                ) : null}
              </div>
              <div className="grid shrink-0 grid-cols-2 gap-2 border-t border-white/10 bg-black/80 p-3">
                <button
                  className="flex min-h-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] px-3 text-sm font-bold text-sdf-text-primary transition hover:bg-white/[0.08]"
                  onClick={() => {
                    setDraftFilters({});
                    onFilterChange({});
                    setFiltersOpen(false);
                  }}
                  type="button"
                >
                  Clear
                </button>
                <button
                  className="flex min-h-10 items-center justify-center rounded-xl bg-sdf-action-primary-bg px-3 text-sm font-bold text-sdf-action-primary-fg transition hover:bg-sdf-action-primary-bg-hovered"
                  onClick={() => {
                    onFilterChange(draftFilters);
                    setFiltersOpen(false);
                  }}
                  type="button"
                >
                  Apply
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="pointer-events-none absolute inset-x-6 bottom-6 z-0 h-[42%] max-h-[260px] min-h-[140px] overflow-hidden rounded-3xl sm:inset-x-8 sm:bottom-8">
        <div className="h-full w-full drop-shadow-[0_0_10px_rgba(243,220,134,0.45)]">
          <Line data={chartData} options={chartOptions} />
        </div>
      </div>
    </section>
  );
}

function MetricTile({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className={`${glassTile} p-5`}>
      <div className="flex items-center gap-2 text-sdf-text-secondary">
        {icon}
        <span className="text-xs font-bold uppercase">{label}</span>
      </div>
      <div className="mt-4 text-2xl font-extrabold leading-none tracking-normal text-sdf-text-primary">
        {value}
      </div>
    </div>
  );
}

function MetricsGrid({
  dashboard,
  signups,
}: {
  dashboard: PartnerDashboardResponse;
  signups: PartnerSignupsResponse;
}) {
  return (
    <section className="grid gap-4 md:grid-cols-3">
      <MetricTile
        icon={<Users size={17} />}
        label="Impressions"
        value={dashboard.totalStarted.toLocaleString()}
      />
      <MetricTile
        icon={<Percent size={17} />}
        label="Conversion"
        value={formatConversion(dashboard.totalSignups, dashboard.totalStarted)}
      />
      <MetricTile
        icon={<CalendarDays size={17} />}
        label="Latest signup"
        value={formatDate(signups.signups[0]?.redeemedAt ?? null)}
      />
    </section>
  );
}

function DashboardContent({
  me,
  mainAppUrl,
  dashboard,
  signups,
  attributionSegments,
  filters,
  onFilterChange,
  onSignOut,
}: {
  me: PartnerMeResponse;
  mainAppUrl: string;
  dashboard: PartnerDashboardResponse;
  signups: PartnerSignupsResponse;
  attributionSegments: SignupByUtm[];
  filters: PartnerDashboardFilters;
  onFilterChange: (filters: PartnerDashboardFilters) => void;
  onSignOut: () => void;
}) {
  const [activeSection, setActiveSection] =
    useState<PartnerSection>("overview");

  return (
    <div className="mx-auto grid h-screen w-full max-w-[1280px] grid-rows-[auto_minmax(0,1fr)] gap-4 px-4 py-6 sm:px-6 lg:py-8">
      <header className="flex items-center justify-between gap-4">
        <BrandLockup />
      </header>

      <div className="grid min-h-0 gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
        <PartnerSidebar
          activeSection={activeSection}
          mainAppUrl={mainAppUrl}
          me={me}
          onSectionChange={setActiveSection}
          onSignOut={onSignOut}
        />
        {activeSection === "overview" ? (
          <section className="grid min-h-0 grid-rows-[minmax(0,1fr)_auto] gap-4">
            <PerformancePanel
              dashboard={dashboard}
              signups={signups}
              segments={attributionSegments}
              selectedFilters={filters}
              onFilterChange={onFilterChange}
            />
            <MetricsGrid dashboard={dashboard} signups={signups} />
          </section>
        ) : null}
        {activeSection === "links" ? (
          <LinksPanel mainAppUrl={mainAppUrl} me={me} />
        ) : null}
        {activeSection === "sponsor" ? <SponsorPanel /> : null}
      </div>
    </div>
  );
}

function EmptyAccess({ onSignOut }: { onSignOut: () => void }) {
  return (
    <section className={`${glassPanel} mx-auto w-full max-w-2xl p-6 sm:p-8`}>
      <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#a78bfa]">
        Access required
      </p>
      <h1 className="mt-3 text-[30px] font-extrabold leading-none tracking-normal text-sdf-text-primary">
        Looks like you don't have access.
      </h1>
      <p className="mt-3 max-w-lg text-sm leading-6 text-sdf-text-secondary">
        Ask a Sport.Fun admin to add this account, then refresh the page.
      </p>
      <button
        className="mt-6 inline-flex min-h-10 items-center gap-2 rounded-xl bg-white/[0.08] px-4 text-sm font-bold text-sdf-text-primary transition hover:bg-white/[0.13]"
        onClick={onSignOut}
      >
        <LogOut size={16} />
        Sign out
      </button>
    </section>
  );
}

function LoadingState() {
  return (
    <section className="grid gap-4 sm:grid-cols-3">
      {[0, 1, 2].map((item) => (
        <div key={item} className={`${glassTile} h-32 animate-pulse p-5`}>
          <div className="h-3 w-24 rounded-full bg-white/10" />
          <div className="mt-8 h-7 w-32 rounded-full bg-white/10" />
        </div>
      ))}
    </section>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <section className={`${glassPanel} mx-auto w-full max-w-2xl p-6 sm:p-8`}>
      <p className="text-xs font-bold uppercase text-sdf-text-secondary">
        Partner dashboard
      </p>
      <h1 className="mt-3 text-[30px] font-extrabold leading-none tracking-normal text-sdf-text-primary">
        Unable to load dashboard
      </h1>
      <p className="mt-3 max-w-lg text-sm text-sdf-text-secondary">
        The partner dashboard could not be loaded. Retry once the API session is
        available.
      </p>
      <button
        className="mt-6 inline-flex min-h-10 items-center gap-2 rounded-xl bg-white/[0.08] px-4 text-sm font-bold text-sdf-text-primary transition hover:bg-white/[0.13]"
        onClick={onRetry}
      >
        <RefreshCw size={16} />
        Retry
      </button>
    </section>
  );
}

function Dashboard({ mainAppUrl }: { mainAppUrl: string }) {
  const { logout } = useLogout();
  const [status, setStatus] = useState<LoadState>("idle");
  const [me, setMe] = useState<PartnerMeResponse | null>(null);
  const [dashboard, setDashboard] = useState<PartnerDashboardResponse | null>(
    null,
  );
  const [signups, setSignups] = useState<PartnerSignupsResponse | null>(null);
  const [attributionSegments, setAttributionSegments] = useState<SignupByUtm[]>(
    [],
  );
  const [filters, setFilters] = useState<PartnerDashboardFilters>({});

  const load = useCallback(
    async (nextFilters: PartnerDashboardFilters = {}) => {
      setStatus("loading");
      try {
        const meResult = await partnersApi.getMe();
        setMe(meResult);

        const [dashboardResult, signupsResult] = await Promise.all([
          partnersApi.getDashboard(nextFilters),
          partnersApi.getSignups(nextFilters),
        ]);
        setDashboard(dashboardResult);
        setSignups(signupsResult);
        if (attributionFilterKey(nextFilters) === "") {
          setAttributionSegments(dashboardResult.signupsByUtm);
        }
        setStatus("ready");
      } catch (error) {
        setStatus(
          error instanceof Error && error.message.includes("not assigned")
            ? "forbidden"
            : "error",
        );
      }
    },
    [],
  );

  useEffect(() => {
    void load({});
  }, [load]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void load(filters);
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [filters, load]);

  const onFilterChange = useCallback(
    (nextFilters: PartnerDashboardFilters) => {
      setFilters(nextFilters);
      void load(nextFilters);
    },
    [load],
  );

  return (
    <AppFrame>
      {status === "ready" && me && dashboard && signups ? (
        <DashboardContent
          me={me}
          mainAppUrl={mainAppUrl}
          dashboard={dashboard}
          signups={signups}
          attributionSegments={attributionSegments}
          filters={filters}
          onFilterChange={onFilterChange}
          onSignOut={logout}
        />
      ) : (
        <div className="mx-auto flex min-h-screen w-full max-w-[1180px] flex-col gap-4 px-4 py-8 sm:px-6">
          <TopNav />

          <div className="flex flex-1 flex-col justify-center gap-4">
            {status === "loading" || status === "idle" ? (
              <LoadingState />
            ) : null}
            {status === "forbidden" ? <EmptyAccess onSignOut={logout} /> : null}
            {status === "error" ? (
              <ErrorState onRetry={() => void load(filters)} />
            ) : null}
          </div>
        </div>
      )}
    </AppFrame>
  );
}

function AuthenticatedApp({ mainAppUrl }: { mainAppUrl: string }) {
  const { authenticated, ready } = usePrivy();
  if (!ready) {
    return (
      <AppFrame>
        <div className="grid min-h-screen place-items-center text-sm text-sdf-text-secondary">
          Loading...
        </div>
      </AppFrame>
    );
  }
  return authenticated ? (
    <Dashboard mainAppUrl={mainAppUrl} />
  ) : (
    <LoginScreen />
  );
}

export default function App() {
  const [status, setStatus] = useState<BootstrapState>("idle");
  const [publicConfig, setPublicConfig] = useState<PublicConfig | null>(null);

  const loadPublicConfig = useCallback(async () => {
    setStatus("loading");

    try {
      const nextConfig = await fetchPublicConfig();
      setPublicConfig(nextConfig);
      setStatus("ready");
    } catch (error) {
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    void loadPublicConfig();
  }, [loadPublicConfig]);

  if (status !== "ready" || !publicConfig) {
    return (
      <AppFrame>
        <div className="mx-auto flex min-h-screen w-full max-w-[1180px] flex-col gap-4 px-4 py-8 sm:px-6">
          <TopNav />

          <div className="flex flex-1 flex-col justify-center gap-4">
            {status === "loading" || status === "idle" ? (
              <LoadingState />
            ) : null}
            {status === "error" ? (
              <ErrorState onRetry={() => void loadPublicConfig()} />
            ) : null}
          </div>
        </div>
      </AppFrame>
    );
  }

  return (
    <PrivyProvider
      appId={publicConfig.privy.PRIVY_APP_ID}
      config={{
        appearance: {
          theme: "dark",
          accentColor: "#2563eb",
          logo: `${publicConfig.sdfPlatform.APP_URL.replace(/\/$/, "")}/favicon.png`,
        },
        loginMethods: ["email", "google", "twitter", "discord", "wallet"],
      }}
    >
      <AuthenticatedApp mainAppUrl={publicConfig.sdfPlatform.APP_URL} />
    </PrivyProvider>
  );
}
